import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/hooks/useToast';
import { submitVibeEdit } from '@/lib/apis';

const AUTO_SECTION = '__auto';
const MIN_INSTRUCTION = 3;
const MAX_INSTRUCTION = 1000;

interface VibeEditBoxProps {
  publicId: string;
  organizationId: number;
  sections: string[];
  editInProgress: boolean;
  lastEditError: string | null;
  onSubmitted: () => void;
}

export function VibeEditBox({
  publicId,
  organizationId,
  sections,
  editInProgress,
  lastEditError,
  onSubmitted,
}: VibeEditBoxProps) {
  const [instruction, setInstruction] = useState('');
  const [section, setSection] = useState<string>(AUTO_SECTION);

  const mutation = useMutation({
    mutationFn: () =>
      submitVibeEdit(publicId, organizationId, instruction.trim(), section === AUTO_SECTION ? null : section),
    onSuccess: () => {
      setInstruction('');
      onSubmitted();
    },
    onError: (error) => {
      toast({
        title: 'Failed to submit edit',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    },
  });

  const busy = editInProgress || mutation.isPending;
  const canSubmit = instruction.trim().length >= MIN_INSTRUCTION && !busy;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-gray-900">
        <Sparkles className="h-4 w-4 text-[#290BD9]" />
        <span className="font-medium">Vibe Edit</span>
      </div>

      {/* A failed edit (gate/build rejection) leaves the live page unchanged; surface it inline. */}
      {lastEditError && !editInProgress && (
        <div className="border-l-4 border-[#FCA5A5] bg-[#FEE2E2] text-[#B91C1C] p-3 text-sm">
          Edit not applied: {lastEditError}
        </div>
      )}

      <Textarea
        value={instruction}
        onChange={(e) => setInstruction(e.target.value.slice(0, MAX_INSTRUCTION))}
        placeholder="Describe the change, e.g. Make the hero headline shorter and change the CTA to 'Get My Free Quote'"
        disabled={busy}
        rows={3}
        className="resize-none"
      />

      <div className="flex items-center justify-between gap-3">
        {sections.length > 0 ? (
          <Select value={section} onValueChange={setSection} disabled={busy}>
            <SelectTrigger className="h-9 w-56 text-gray-900">
              <SelectValue placeholder="Auto (recommended)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO_SECTION}>Auto (recommended)</SelectItem>
              {sections.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <span />
        )}

        <Button
          onClick={() => mutation.mutate()}
          disabled={!canSubmit}
          className="h-9 bg-[#290BD9] hover:bg-[#2309C5] text-white"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Applying...
            </>
          ) : (
            'Apply Edit'
          )}
        </Button>
      </div>
    </div>
  );
}
